import { createContext, useContext, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import type { Engine } from '@core/engine';
import type { GameState } from '@core/state';
import type { Action } from '@core/actions';

/**
 * The store bridge (TECHNICAL_APPROACH §6). React binds to the engine's external
 * store via useSyncExternalStore (tearing-free). Components are pure renders of
 * the snapshot and dispatch actions; they never mutate state.
 */
const EngineContext = createContext<Engine | null>(null);

export function EngineProvider({ engine, children }: { engine: Engine; children: ReactNode }) {
  return <EngineContext.Provider value={engine}>{children}</EngineContext.Provider>;
}

export function useEngine(): Engine {
  const engine = useContext(EngineContext);
  if (!engine) throw new Error('useEngine must be used within an EngineProvider');
  return engine;
}

/** Subscribe to the engine's monotonic snapshot token (stable identity). */
export function useGameVersion(): number {
  const engine = useEngine();
  return useSyncExternalStore(engine.subscribe, engine.getSnapshot, engine.getSnapshot);
}

/**
 * Subscribe to the store and derive a value from live state via a selector.
 * We subscribe to the version token (a number, so getSnapshot stays cached) and
 * read the selector against live state during render, correct for our
 * single-threaded host and avoids the "getSnapshot should be cached" pitfall.
 */
export function useGame<T>(selector: (s: GameState) => T): T {
  const engine = useEngine();
  useGameVersion();
  return selector(engine.getState());
}

/** Convenience: get the dispatch fn. */
export function useDispatch(): (action: Action) => void {
  return useEngine().dispatch;
}
